import { BUILD_PROPERTIES } from '@/editor/constant/buildProperties';
import { globalStores } from '@/editor/stores';
import { EEntityType, EOperateMode, EToolType, IOperateInfo } from '@/editor/types';
import {
  EParamGroupLabel,
  EParamLabel,
  EParamsKeys,
  EParamType,
  EParamUnit,
  IEditParams,
  IEditParamsOnChange
} from '@/editor/types/editParamType';
import { getUniqueId } from '@/editor/utils';
import type { ITransformingInfo } from '@/editor/stores/EditorStateStore';
import { getAreaInfo } from '@/editor/utils/room/area';

import { ILibraryModel } from '@/service/library/types';

/** 旋转/缩放拖拽中优先取editorStateStore的临时数据，保证面板实时显示拖拽中间值 */
export function getTransformingData<T extends ITransformingInfo['data']>(
  entityId: string,
  type: EEntityType
): T | undefined {
  const transforming = globalStores.editorStateStore.transformingData;
  if (transforming && transforming.type === type && transforming.data.id === entityId) {
    return transforming.data as T;
  }
  return undefined;
}

export function getLibraryModelBaseInfo(modelInfo: ILibraryModel) {
  const { name } = modelInfo;
  const { thumbnail } = modelInfo.display;
  const { brand, size, price } = modelInfo.properties;

  return {
    brand,
    size,
    thumbnail,
    price,
    name
  };
}

export function getParamEditBehavior(key: string) {
  let behavior = EParamsKeys.empty;
  switch (key) {
    case EParamsKeys.width:
      behavior = EParamsKeys.width;
      break;
    case EParamsKeys.height:
      behavior = EParamsKeys.height;
      break;
    case EParamsKeys.offGround:
      behavior = EParamsKeys.offGround;
      break;
    case EParamsKeys.length:
      behavior = EParamsKeys.length;
      break;
    case EParamsKeys.diameter:
      behavior = EParamsKeys.diameter;
      break;
    case EParamsKeys.rotation:
      behavior = EParamsKeys.rotation;
      break;
  }

  return behavior;
}

export function getRoomAreaInfo() {
  const roomDatas = globalStores.roomStore.getRoomDatas();

  let coreArea = 0;
  for (const roomData of roomDatas) {
    const { coreArea: roomCoreArea } = getAreaInfo(roomData);
    coreArea += roomCoreArea;
  }

  return {
    innerArea: 0,
    coreArea: Number(coreArea.toFixed(2))
  };
}

export function getParamTitle(type: EEntityType | EOperateMode) {
  switch (type) {
    case EOperateMode.DrawWall:
    case EOperateMode.DrawRectWall:
      return EParamGroupLabel.DRAWWALL;
    case EOperateMode.DrawArcWall:
      return EParamGroupLabel.ARC_WALL;
    case EEntityType.Wall:
      return EParamGroupLabel.WALL;
    case EEntityType.DoorWindow:
      return EParamGroupLabel.SINGLEDOOR;
    case EEntityType.Room:
      return EParamGroupLabel.ROOM;
    case EOperateMode.EMPTY:
      return EParamGroupLabel.SPACE;
    default:
      return EParamGroupLabel.SPACE;
  }
}

export function isDrawWallMode(operateMode: EOperateMode) {
  const drawWallMode = [EOperateMode.DrawWall, EOperateMode.DrawRectWall, EOperateMode.DrawArcWall];
  return drawWallMode.includes(operateMode);
}

export function isDrawSeparateWallMode(operateInfo: IOperateInfo) {
  const { mode, toolType } = operateInfo;
  const drawSeparateWallMode = [EOperateMode.DrawSeparateWall, EOperateMode.DrawArcSeparateWall];
  if (drawSeparateWallMode.includes(mode) && toolType !== EToolType.virtualSeparateWall) {
    return true;
  }

  return false;
}

export function hasStructModel() {
  const roomDatas = globalStores.roomStore.getRoomDatas();
  const wallDatas = globalStores.wallStore.getWallDatas();
  const columnDatas = globalStores.structStore.getColumnDatas();

  return roomDatas.length > 0 || wallDatas.length > 0 || columnDatas.length > 0;
}

export function getDefaultParamInfo() {
  const { coreArea } = getRoomAreaInfo();
  const paramDatas: IEditParams = {
    id: getUniqueId(), // 这里无意义
    type: EOperateMode.EMPTY,
    title: getParamTitle(EOperateMode.EMPTY),
    group: [
      {
        id: EParamGroupLabel.AREA,
        label: EParamGroupLabel.AREA,
        fields: [EParamsKeys.coreArea]
      },
      {
        id: EParamGroupLabel.FLOORS,
        label: EParamGroupLabel.FLOORS,
        fields: [EParamsKeys.layerHeight]
      }
    ],
    values: {
      [EParamsKeys.coreArea]: coreArea,
      [EParamsKeys.layerHeight]: globalStores.paramStore.layerHeight
    },
    fields: {
      [EParamsKeys.coreArea]: {
        type: EParamType.NUMBER,
        unit: EParamUnit.AREA,
        label: EParamLabel.CORE_AREA,
        settings: {
          readonly: true
        }
      },
      [EParamsKeys.layerHeight]: {
        type: EParamType.NUMBER,
        unit: EParamUnit.MM,
        label: EParamLabel.LAYER_HEIGHT,
        settings: {
          readonly: false,
          min: BUILD_PROPERTIES.MIN_INPUT
        }
      }
    }
  };

  return paramDatas;
}

/** values 里可能存着材质、构件这类对象，String 之后都是 [object Object]，据此判等会把真实改动吃掉 */
export function isParamValueUnchanged(params: Parameters<IEditParamsOnChange>[0]) {
  const current = params.values[params.key];
  if (typeof current === 'object') return false;
  return String(current) === String(params.value);
}

export function getParamsChangeBehaviors(params: Parameters<IEditParamsOnChange>[0]) {
  const behaviors: EParamsKeys[] = [];

  const behavior = params.key;
  let mainBehavior: EParamsKeys;
  if (behavior !== EParamsKeys.empty) {
    mainBehavior = behavior;
    behaviors.push(behavior);
  } else {
    return;
  }

  if (params.linked) {
    for (const link of params.linked) {
      const behavior = link;
      if (behavior !== EParamsKeys.empty && mainBehavior !== behavior) {
        behaviors.push(behavior);
      }
    }
  }

  return behaviors;
}
